import React, { useState, useMemo } from "react";

// Curated stakeholder groups for AgriLink
const STAKEHOLDERS = [
  {
    id: "farmRegionalCoordinator",
    label: "Farm Regional Co-ordinator",
    emoji: "🌍",
    pains: [
      "Challenges in coordinating large-scale farm activities across regions.",
      "Limited access to real-time data on crop yields and market trends.",
      "Difficulty in connecting farmers with buyers and service providers.",
      "Inefficient tracking of regional productivity and compliance."
    ],
    opportunities: [
      "Leverage Data-Driven Insights and Analytics for regional monitoring.",
      "Utilize Community & Social tools for farmer engagement and events.",
      "Access Agri Marketplace for seamless supply chain coordination.",
      "Track Recognition & Progress with SAKSHAM Ranking and leaderboards."
    ]
  },
  {
    id: "areaCoordinator",
    label: "Area Co-ordinator",
    emoji: "📍",
    pains: [
      "Fragmented communication between local farmers and stakeholders.",
      "Delays in supply chain due to poor visibility into local deals.",
      "Struggles with training and upskilling area-level workers.",
      "Limited tools for nearby resource allocation and emergency support."
    ],
    opportunities: [
      "Use Nearby Deals and ThinkXpress for quick local deliveries.",
      "Engage via Agri Events and Workshops for area-specific training.",
      "Monitor progress with Task & Management tools like Attendance.",
      "Foster innovation through Agri Innovations hub."
    ]
  },
  {
    id: "clusterCoordinator",
    label: "Cluster Co-ordinator",
    emoji: "🔗",
    pains: [
      "Inefficiencies in cluster-based farming collaborations.",
      "Challenges in managing shared resources like rentals and logistics.",
      "Low adoption of digital tools for cluster-wide decision-making.",
      "Difficulties in resolving complaints and enquiries at scale."
    ],
    opportunities: [
      "Streamline with Contract Farming and Unity Deals features.",
      "Access Rental and Services via Agri Marketplace.",
      "Build community through POV / Debate and Immersive Presentations.",
      "Earn via Digital Income opportunities like Refer and Resell."
    ]
  },
  {
    id: "farmer",
    label: "Farmer",
    emoji: "🌱",
    pains: [
      "Unpredictable market prices and access to fair buyers.",
      "Limited financial tools for loans, insurance, and daily management.",
      "Lack of modern tech like digital twins for farm optimization.",
      "Isolation from learning resources and peer support networks."
    ],
    opportunities: [
      "Secure AgriLoan, AgriInsurance, and Wholesale access.",
      "Innovate with Digital Twin, Vertical Farming, and Metaverse tools.",
      "Learn via My Agri Knowledge, Workshops, and AI Counselling.",
      "Engage in Fun & Games and Aalu Dalo Sona Nikalo contests."
    ]
  },
  {
    id: "workers",
    label: "Workers",
    emoji: "👨‍🌾",
    pains: [
      "Irregular job opportunities and farm employment tracking.",
      "Challenges in skill development for specialized agri roles.",
      "Low visibility into safe, rewarding work environments.",
      "Difficulties in daily task logging and attendance verification."
    ],
    opportunities: [
      "Find jobs via Farm Jobs and Bluehands platform.",
      "Upskill with Digital Learning resources like Podcasts.",
      "Track daily activities with Farmer's Daily Life and Diary.",
      "Earn rewards through Rewarding Ads and Videos."
    ]
  },
  {
    id: "agriStockist",
    label: "Agri Stockist",
    emoji: "📦",
    pains: [
      "Inventory management issues in volatile agri markets.",
      "Delays in packaging and storage for perishable goods.",
      "Limited digital channels for stock visibility and sales.",
      "Challenges in handling enquiries and complaints efficiently."
    ],
    opportunities: [
      "Optimize with Agriproducts Packaging and Storage Service.",
      "Sell via Catalogue, Products, and Wholesale Commodity Market.",
      "Connect through Enquiry and Complaints management tools.",
      "Gain insights from Analytics & Insights dashboards."
    ]
  },
  {
    id: "agriRetailer",
    label: "Agri Retailer",
    emoji: "🏬",
    pains: [
      "Competition from unorganized markets and low footfall.",
      "Inefficient sourcing from wholesalers and nearby suppliers.",
      "Struggles with digital payments and customer loyalty programs.",
      "Lack of tools for promotional events and community outreach."
    ],
    opportunities: [
      "Source via Unity Deals, Nearby Deals, and Rental options.",
      "Promote with Agri Events and Immersive Presentations.",
      "Build loyalty through Kharcho Jeeto and Rewarding programs.",
      "Scale with Import-Export and Agri Real Assets."
    ]
  },
  {
    id: "otherSellers",
    label: "Other Sellers",
    emoji: "🛒",
    pains: [
      "Fragmented access to diverse buyer networks beyond traditional channels.",
      "Challenges in pricing and negotiating deals digitally.",
      "Limited visibility for niche agri products or services.",
      "Inefficiencies in handling bulk orders and custom enquiries."
    ],
    opportunities: [
      "List products via Agri Marketplace Catalogue and Create Basket.",
      "Connect with buyers through Nearby Deals and Unity Deals.",
      "Engage in Resell and Referral Income for expanded reach.",
      "Utilize Agri Innovations for unique selling propositions."
    ]
  },
  {
    id: "logistic",
    label: "Logistic",
    emoji: "🚚",
    pains: [
      "Delays and tracking issues in perishable goods transportation.",
      "High costs due to inefficient route planning and load optimization.",
      "Challenges in integrating with farm-to-fork supply chains.",
      "Limited real-time visibility into delivery status and complaints."
    ],
    opportunities: [
      "Streamline with ThinkXpress Quick Delivery and Wholesale features.",
      "Partner via Rental and Services in Agri Marketplace.",
      "Track via Data-Driven Insights and Analytics dashboards.",
      "Collaborate through Community & Social for logistics events."
    ]
  },
  {
    id: "storageService",
    label: "Storage Service",
    emoji: "📦",
    pains: [
      "Capacity constraints during peak harvest seasons.",
      "Quality degradation due to inadequate climate control.",
      "Difficulties in inventory tracking and space allocation.",
      "Challenges in connecting with sellers for timely pickups."
    ],
    opportunities: [
      "Enhance with Agriproducts Packaging integration.",
      "Manage via Task & Management tools for scheduling.",
      "Connect sellers through Enquiry and Complaints systems.",
      "Gain efficiency from Quick & Reliable supply chain models."
    ]
  },
  {
    id: "exportImport",
    label: "Export-Import",
    emoji: "🚢",
    pains: [
      "Regulatory hurdles and documentation delays for international trade.",
      "Fluctuating global prices and compliance with standards.",
      "Limited access to verified international buyers and sellers.",
      "Challenges in forecasting demand and managing logistics."
    ],
    opportunities: [
      "Facilitate via Import-Export module in Agri Marketplace.",
      "Leverage Data-Driven Insights for market trends.",
      "Build networks through Community Impact and global forums.",
      "Innovate with Agri Innovations like Digital Twin for quality assurance."
    ]
  },
  {
    id: "foodBusiness",
    label: "Food Business",
    emoji: "🍽️",
    pains: [
      "Sourcing fresh, traceable ingredients consistently.",
      "Wastage from supply inconsistencies and short shelf life.",
      "Regulatory compliance for food safety and labeling.",
      "Challenges in scaling operations with reliable suppliers."
    ],
    opportunities: [
      "Source via Contract Farming and Agri Real Assets.",
      "Reduce waste with Quick & Reliable delivery and Storage.",
      "Ensure traceability through Trust & Transparency features.",
      "Engage suppliers via Agri Marketplace Services."
    ]
  },
  {
    id: "wasteDisposer",
    label: "Waste Disposer",
    emoji: "🚮",
    pains: [
      "Inefficient collection and segregation of agri waste.",
      "Limited recycling or upcycling opportunities for organic waste.",
      "High costs for disposal and environmental compliance.",
      "Lack of partnerships for sustainable waste management."
    ],
    opportunities: [
      "Collaborate via Community & Social for green initiatives.",
      "Innovate with Agri Innovations for waste-to-value solutions.",
      "Track via Analytics & Insights for impact measurement.",
      "Partner with NGOs and Corporates for funding and scaling."
    ]
  },
  {
    id: "ngo",
    label: "NGO",
    emoji: "🤝",
    pains: [
      "Difficulty scaling impact in rural agri communities.",
      "Challenges in monitoring program effectiveness and farmer adoption.",
      "Limited partnerships for sustainable funding and resources.",
      "Gaps in providing tech-enabled training and support."
    ],
    opportunities: [
      "Collaborate via Community & Social forums and Support a Farmer.",
      "Track impact with Recognition & Progress tools and SAKSHAM Ranking.",
      "Empower through Digital Learning and AI Counselling.",
      "Fundraise via Corporate partnerships and Innovation First initiatives."
    ]
  },
  {
    id: "corporate",
    label: "Corporate",
    emoji: "🏢",
    pains: [
      "Complex CSR implementation in agri supply chains.",
      "Measuring ROI on sustainability and community programs.",
      "Integration challenges with local farmers and suppliers.",
      "Limited visibility into ethical sourcing and transparency."
    ],
    opportunities: [
      "Align CSR with Trust & Transparency and Community Impact features.",
      "Partner on Data-Driven Insights for supply chain analytics.",
      "Engage in Quick & Reliable delivery models.",
      "Innovate with Agri Innovations like Drone Operators and Experts."
    ]
  },
  {
    id: "agriScholar",
    label: "Agri Scholar",
    emoji: "📚",
    pains: [
      "Limited access to cutting-edge agri research and case studies.",
      "Challenges in applying academic knowledge to practical farming.",
      "Isolation from industry experts and real-world collaborations.",
      "Difficulty in publishing or sharing scholarly work digitally."
    ],
    opportunities: [
      "Access My Agri Knowledge and Agri Podcast resources.",
      "Collaborate via POV / Debate and Visualbrief Presentations.",
      "Contribute to Agri Innovations and Think5Whys Aggregator.",
      "Track scholarly impact with Analytics & Insights."
    ]
  },
  {
    id: "droneOperators",
    label: "Drone Operators",
    emoji: "🚁",
    pains: [
      "Regulatory restrictions and certification hurdles for agri drones.",
      "Limited demand visibility from farmers for precision services.",
      "Challenges in data analysis from drone-captured imagery.",
      "High equipment costs and maintenance without steady gigs."
    ],
    opportunities: [
      "Offer services via Agri Marketplace Rental and Services.",
      "Connect with farmers through Community Events and Forums.",
      "Analyze data with Digital Twin and AI Counselling tools.",
      "Earn via Digital Income Refer and Resell programs."
    ]
  },
  {
    id: "expert",
    label: "Expert",
    emoji: "🧑‍🔬",
    pains: [
      "Fragmented access to clients needing specialized agri advice.",
      "Challenges in delivering remote consultations effectively.",
      "Low monetization for knowledge-sharing sessions.",
      "Difficulty in building credibility without digital portfolios."
    ],
    opportunities: [
      "Provide expertise via Immersive Presentations and Workshops.",
      "Monetize through Expert Mentoring in Digital Learning.",
      "Showcase via Agri Innovations hub and POV Debates.",
      "Network in Community & Social for collaborations."
    ]
  },
  {
    id: "vetDoctor",
    label: "Vet Doctor",
    emoji: "🐄",
    pains: [
      "Remote diagnostics for livestock in rural areas.",
      "Supply shortages of vaccines and treatments during outbreaks.",
      "Challenges in tracking animal health records digitally.",
      "Limited outreach to smallholder farmers for preventive care."
    ],
    opportunities: [
      "Use AI Counselling and Digital Twin for remote vet support.",
      "Access AgriInsurance integrations for health claims.",
      "Engage via Farmer's Daily Life and Attendance tracking.",
      "Collaborate through Agri Events and Community Forums."
    ]
  },
  {
    id: "wholesaleBuyer",
    label: "Wholesale Buyer",
    emoji: "🏪",
    pains: [
      "Inconsistent quality and supply volumes from producers.",
      "Price volatility and negotiation delays in bulk purchases.",
      "Logistics bottlenecks in transporting large consignments.",
      "Limited transparency in sourcing ethical and sustainable goods."
    ],
    opportunities: [
      "Source reliably via Wholesale Commodity Market and Contract Farming.",
      "Ensure quality with Trust & Transparency and Data-Driven Insights.",
      "Streamline logistics through Import-Export and Quick Delivery.",
      "Build long-term ties via Community Impact initiatives."
    ]
  },
  {
    id: "retailBuyer",
    label: "Retail Buyer",
    emoji: "🛒",
    pains: [
      "High prices due to fragmented supply chains.",
      "Concerns over freshness and traceability of agri products.",
      "Inconvenient access to variety and personalized shopping.",
      "Challenges in discovering local or seasonal deals."
    ],
    opportunities: [
      "Shop conveniently via Create Basket and Nearby Deals.",
      "Verify freshness with Quick & Reliable features.",
      "Discover variety in Catalogue and Unity Deals.",
      "Save with Digital Income rewards like Kharcho Jeeto."
    ]
  },
  {
    id: "consumerBuyer",
    label: "Consumer Buyer",
    emoji: "👨‍👩‍👧‍👦",
    pains: [
      "Overwhelmed by choices without clear sustainability info.",
      "Delivery delays and packaging waste in online orders.",
      "Limited awareness of supporting local farmers directly.",
      "Budget constraints on premium or organic produce."
    ],
    opportunities: [
      "Support locals via Support a Farmer and Agri Real Assets.",
      "Enjoy eco-friendly options with Waste Disposer integrations.",
      "Order via Home Basket and ThinkXpress for fast, green delivery.",
      "Earn rewards through Rewarding Videos and Games."
    ]
  },
  {
    id: "think5whysAggregator",
    label: "Think5Whys Aggregator (Self Employment)",
    emoji: "🏆",
    pains: [
      "Scaling self-employment opportunities in agri ecosystem.",
      "Challenges in aggregating diverse services under one brand.",
      "Low visibility for aggregator-led initiatives.",
      "Difficulties in measuring and showcasing impact."
    ],
    opportunities: [
      "Aggregate via Agri Marketplace and Innovation hub.",
      "Promote through Recognition & Progress leaderboards.",
      "Engage community with Social Forums and Events.",
      "Track self-employment growth with Analytics dashboards."
    ]
  }
];

// Key USPs for AgriLink
const USPs = [
  {
    id: "sustainableGrowth",
    title: "Sustainable Growth",
    desc: "Empowering farmers, traders, and consumers with a unified platform that drives inclusive growth.",
    emoji: "🌱",
  },
  {
    id: "trustTransparency",
    title: "Trust & Transparency",
    desc: "Direct connect between producers, wholesalers, retailers, and end consumers without hidden middle layers.",
    emoji: "🤝",
  },
  {
    id: "dataDrivenInsights",
    title: "Data-Driven Insights",
    desc: "Leverage analytics and AI to make better agri-business decisions and maximize productivity.",
    emoji: "📊",
  },
  {
    id: "quickReliable",
    title: "Quick & Reliable",
    desc: "Efficient supply chain and delivery models that minimize wastage and maximize freshness.",
    emoji: "🚚",
  },
  {
    id: "innovationFirst",
    title: "Innovation First",
    desc: "Smart contracts, digital cards, gamified contests, and reward-based engagement for all stakeholders.",
    emoji: "💡",
  },
  {
    id: "communityImpact",
    title: "Community Impact",
    desc: "Strengthening Bharat’s agricultural backbone with scalable models for rural to urban participation.",
    emoji: "🌍",
  },
];

const Pill = ({ children }) => (
  <span className="inline-block bg-green-700/30 text-green-100 px-2 py-0.5 rounded-full text-xs font-medium">
    {children}
  </span>
);

const ExpandIcon = ({ open }) => (
  <span className={`ml-2 text-sm ${open ? "rotate-180" : "rotate-0"} transition-transform`}>
    ▼
  </span>
);

export default function AgriLinkInsights() {
  const [tab, setTab] = useState("stakeholders"); 
  const [query, setQuery] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  const filteredStakeholders = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAKEHOLDERS;
    return STAKEHOLDERS.filter(
      (s) =>
        s.label.toLowerCase().includes(q) ||
        s.pains.some((p) => p.toLowerCase().includes(q)) ||
        s.opportunities.some((o) => o.toLowerCase().includes(q))
    );
  }, [query]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-green-950 p-4 md:p-8 text-gray-100">
      {/* Header */}
      <header className="mx-auto mb-2 text-center">
        <h1 className="text-lg md:text-xl font-bold text-green-400">🌱 AgriLink Insights</h1>
        <p className="text-gray-300 mt-2 text-sm md:text-base">
          An empowerment & transformation engine for agriculture and farm future. Extending Digital India for Continuous Digital Income, Learning & Productivity!
        </p>
      </header>

      {/* Tabs */}
      <nav className="grid grid-cols-2 md:grid-cols-2 gap-2 justify-center mb-2 place-items-center">
        <button
          onClick={() => setTab("stakeholders")}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "stakeholders"
              ? "bg-green-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          👨‍🌾 Stakeholders
        </button>

        <button
          onClick={() => setTab("usps")}
          className={`flex items-center justify-center gap-1 px-3 py-2 rounded-md font-semibold text-sm transition-all ${
            tab === "usps"
              ? "bg-green-600 text-black shadow"
              : "bg-gray-800 text-gray-200 hover:bg-gray-700"
          }`}
        >
          💡 USPs
        </button>
      </nav>

      {/* Search */}
      <div className="flex flex-col md:flex-row justify-center gap-2 mb-2 place-items-center">
        <input
          aria-label="Search stakeholders"
          placeholder="Search stakeholders, pain or opportunity..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="bg-gray-800 text-gray-200 placeholder-gray-400 px-3 py-2 rounded-md text-sm w-full md:w-80 focus:outline-none focus:ring-2 focus:ring-green-500"
        />
        <button
          onClick={() => { setQuery(""); setExpandedId(null); }}
          className="text-sm mt-4 w-20 bg-gray-700 px-3 py-2 rounded-md hover:bg-gray-600"
        >
          Reset
        </button>
      </div>

      <main className="max-w-6xl mx-auto space-y-6">
        {/* Stakeholders */}
        {tab === "stakeholders" && (
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {filteredStakeholders.map((s) => {
              const isOpen = expandedId === s.id;
              return (
                <article key={s.id} className="bg-gradient-to-br from-gray-800 to-green-900/30 rounded-lg border border-green-800/30 shadow-sm overflow-hidden">
                  <div className="p-4 flex items-start gap-3">
                    <div className="text-3xl">{s.emoji}</div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-m font-semibold text-green-300">{s.label}</h3>
                        <button
                          onClick={() => setExpandedId(isOpen ? null : s.id)}
                          className="ml-2 bg-green-700/30 text-sm w-32 px-2 py-1 rounded"
                        >
                          {isOpen ? "Collapse" : "Expand"} <ExpandIcon open={isOpen} />
                        </button>
                      </div>
                      <p className="text-sm text-gray-300 mt-2 line-clamp-3">
                        <strong>Pain points:</strong> {s.pains[0]} • {s.pains[1]} • {s.pains[2]}
                      </p>
                      {isOpen && (
                        <div className="mt-3 space-y-3 text-sm text-gray-200">
                          <div>
                            <h4 className="font-medium text-green-300">Pain Points</h4>
                            <ol className="list-decimal list-inside mt-1 space-y-1">
                              {s.pains.map((p, idx) => <li key={idx}>{p}</li>)}
                            </ol>
                          </div>
                          <div>
                            <h4 className="font-medium text-green-300">Opportunities</h4>
                            <ul className="list-disc list-inside mt-1 space-y-1">
                              {s.opportunities.map((o, idx) => <li key={idx}>{o}</li>)}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
            {filteredStakeholders.length === 0 && (
              <div className="bg-gray-800 p-4 rounded text-center text-gray-300">No results found for your search.</div>
            )}
          </section>
        )}

        {/* USPs */}
        {tab === "usps" && (
          <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {USPs.map((u) => (
              <div key={u.id} className="bg-gradient-to-br from-green-700/20 to-gray-800 rounded-lg p-4 border border-green-600/20">
                <div className="flex items-start gap-3">
                  <div className="text-2xl">{u.emoji}</div>
                  <div>
                    <h3 className="font-semibold text-lg text-green-300">{u.title}</h3>
                    <p className="text-sm text-gray-200 mt-2">{u.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}
      </main>

      <footer className="max-w-6xl mx-auto mt-8 text-center text-sm text-gray-400">
        Think5Whys AgriLink • Empowering agriculture with Digital Income, Learning, Innovation, and Community 🌱
      </footer>
    </div>
  );
}